import React, { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { get } from "lodash";
import { HelpId, HELP_SYSTEM_URL } from "shared/helpSystemBindings";
import { isNotEmpty } from "utils/validate";
import { getNextMaxId } from "utils/data";
import useMessageTimer from "hooks/useMessageTimer";
import { getSourcesForFilter } from "./utils";
import Table from "./components/Table";
import { TableElementFormField } from "../../../ElementDialog";
import Button, { ButtonStyle } from "components/Button";
import ScrollBar from "components/ScrollBar";
import RowDialog from "./components/RowDialog";
import ColumnDialog from "./components/ColumnDialog";
import ResultInfoMessage, { ResultInfoMessageType } from "components/ResultInfoMessage";
import LearnMoreLink from "rs-components/LearnMoreLink";
import { parameterTypesSelector, processVariablesSelector } from "store/global/global.selectors";
import { ResultElementSourceType, TableElementSourceType, } from "store/rs-workspace/rs-workspace.types";
import { incompleteSimulationsIdsSelector, workspaceCellModelsSelector, workspaceInsightsSelector, workspaceOptimizationsSelector, workspaceProcessDataSelector, workspaceRecipesSelector, workspaceSimulationsSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { getAvailableFilteredCellModels, getAvailableFilteredProcessData, getAvailableFilteredRecipes, getAvailableProcessVariableCategories, getAvailableSimulations, getPossibleSimulationVariantOptimizationPairs, } from "store/rs-workspace/utils/results.utils";
import * as styles from "./styles.module.scss";
const MAX_ROWS = 6;
const MAX_COLUMNS = 8;
const TableElement = ({ resultSetId, elementId, form, elementData }) => {
    const [rowDialogOpen, setRowDialogOpen] = useState(false);
    const [columnDialogOpen, setColumnDialogOpen] = useState(false);
    const viewSelectRefs = useRef([]);
    const columnThreeDotsMenuRefs = useRef([]);
    const processData = useSelector(workspaceProcessDataSelector);
    const simulations = useSelector(workspaceSimulationsSelector);
    const optimizations = useSelector(workspaceOptimizationsSelector);
    const insights = useSelector(workspaceInsightsSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const cellModels = useSelector(workspaceCellModelsSelector);
    const parameterTypes = useSelector(parameterTypesSelector);
    const processVariables = useSelector(processVariablesSelector);
    const incompleteSimulationsIds = useSelector(incompleteSimulationsIdsSelector);
    const [showCopiedToClipboardMessage, setShowCopiedToClipboardMessage] = useMessageTimer();
    const editMode = useMemo(() => !!form, [form]);
    const { availableRecipes, recipesWithFilteredParameters } = useMemo(() => getAvailableFilteredRecipes(recipes, get(elementData, TableElementFormField.columns)?.map((column) => column.parameter), parameterTypes), [recipes, elementData, parameterTypes]);
    const { availableCellModels, cellModelsWithFilteredParameters } = useMemo(() => getAvailableFilteredCellModels(cellModels, get(elementData, TableElementFormField.columns)), [cellModels, elementData]);
    const availableProcessVariableCategories = useMemo(() => getAvailableProcessVariableCategories(processVariables), [processVariables]);
    const availableSimulations = useMemo(() => getAvailableSimulations(simulations, cellModels, recipes), [simulations, cellModels, recipes]);
    const { availableProcessData, filteredProcessData } = useMemo(() => getAvailableFilteredProcessData(processData, getSourcesForFilter(ResultElementSourceType.ProcessData, get(elementData, TableElementFormField.rows)).map(({ processDataId }) => processDataId)), [processData, elementData]);
    const allSimulationVariantOptimizationPairsUsed = useMemo(() => {
        const simulationRows = get(elementData, TableElementFormField.rows).filter(({ sourceType }) => sourceType === TableElementSourceType.SIMULATION);
        const possibleCombinations = availableSimulations.reduce((acc, simulation) => [
            ...acc,
            ...getPossibleSimulationVariantOptimizationPairs(simulation, optimizations, insights, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes),
        ], []);
        return simulationRows.length >= possibleCombinations.length;
    }, [
        elementData,
        availableSimulations,
        optimizations,
        insights,
        processVariables,
        incompleteSimulationsIds,
        simulations,
        cellModels,
        recipes,
    ]);
    const addRowDisabledMessage = useMemo(() => {
        if (availableSimulations.length === 0 && availableProcessData.length === 0) {
            return "No Simulations and Process data to add.";
        }
        else if (allSimulationVariantOptimizationPairsUsed && filteredProcessData.length === 0) {
            return "All possible data sources have already been added.";
        }
    }, [
        availableProcessData.length,
        availableSimulations.length,
        filteredProcessData.length,
        allSimulationVariantOptimizationPairsUsed,
    ]);
    const addColumnDisabledMessage = useMemo(() => {
        if (availableRecipes.length === 0 &&
            availableProcessVariableCategories.length === 0 &&
            availableCellModels.length === 0) {
            return "There are no Parameters to view results.";
        }
        else if (!recipesWithFilteredParameters.length && !cellModelsWithFilteredParameters) {
            return "All possible parameters have already been added.";
        }
    }, [
        availableCellModels.length,
        availableProcessVariableCategories.length,
        availableRecipes.length,
        cellModelsWithFilteredParameters,
        recipesWithFilteredParameters.length,
    ]);
    const handleScroll = useCallback(() => {
        const { current: viewSelectRefsCurrent } = viewSelectRefs;
        if (viewSelectRefsCurrent) {
            viewSelectRefsCurrent.forEach((el) => {
                if (el?.state.isOpen && el.props.popoverProps?.onInteraction) {
                    el.props.popoverProps.onInteraction(false);
                }
            });
        }
        const { current: columnThreeDotsMenuRefsCurrent } = columnThreeDotsMenuRefs;
        if (columnThreeDotsMenuRefsCurrent) {
            columnThreeDotsMenuRefsCurrent.forEach((el) => {
                if (el?.state.isOpen && el.props.onInteraction) {
                    el.props.onInteraction(false);
                }
            });
        }
    }, []);
    const getAddRowColumnButtonDefaultProps = useCallback((key, max, disabledMessage) => ({
        title: (elementData?.[key] || []).length >= max ? `Maximum number of ${key} is ${max}.` : disabledMessage,
        disabled: (elementData?.[key] || []).length >= max || !!disabledMessage,
        label: `Add ${key.slice(0, -1)}`,
        showTooltipIfDisabled: true,
        classes: { root: styles["add-button"] },
    }), [elementData]);
    const upsert = useCallback((values, field) => {
        let newItem;
        if (field === TableElementFormField.rows) {
            const { id, sourceId, sourceType, optimization, adjustmentId } = values;
            newItem = {
                id,
                sourceId: sourceId ?? null,
                sourceType: sourceType || TableElementSourceType.SIMULATION,
                optimizationId: optimization ?? null,
                adjustmentId: adjustmentId ?? null,
                position: 0,
                changed: true,
            };
        }
        else {
            const { columnId, view, parameter } = values;
            newItem = {
                id: columnId,
                view,
                parameter,
                position: 0,
                changed: true,
            };
        }
        if (isNotEmpty(newItem.id)) {
            const index = get(elementData, field).findIndex(({ id }) => id === newItem.id);
            if (index !== -1) {
                const currentItem = get(elementData, field)[index];
                newItem.position = currentItem.position;
                form?.mutators.update(field, index, newItem);
                return;
            }
        }
        newItem.id = getNextMaxId(get(elementData, field));
        newItem.position = getNextMaxId(get(elementData, field), "position");
        form?.mutators.push(field, newItem);
    }, [elementData, form?.mutators]);
    const remove = useCallback((id, field) => {
        const index = get(elementData, field).findIndex((item) => item.id === id);
        if (index !== -1) {
            form?.mutators.remove(field, index);
        }
    }, [elementData, form?.mutators]);
    const move = useCallback((positions, field) => {
        const newSet = positions
            .reduce((acc, { id, position }) => {
            const index = get(elementData, field).findIndex((item) => item.id === id);
            const currentItem = get(elementData, field)[index];
            if (currentItem) {
                acc.push({ ...currentItem, position });
            }
            return acc;
        }, [])
            .sort((a, b) => a.position - b.position);
        form?.change(field, newSet);
    }, [elementData, form]);
    return (React.createElement("div", { className: styles.root },
        editMode && (React.createElement("div", { className: styles.header },
            React.createElement("h3", null, "Edit table"),
            React.createElement(Button, { style: ButtonStyle.Tertiary, onClick: () => setColumnDialogOpen(true), ...getAddRowColumnButtonDefaultProps(TableElementFormField.columns, MAX_COLUMNS, addColumnDisabledMessage) }),
            columnDialogOpen && (React.createElement(ColumnDialog, { columns: get(elementData, TableElementFormField.columns), rows: get(elementData, TableElementFormField.rows), onClose: () => setColumnDialogOpen(false), onSubmit: (values) => upsert(values, TableElementFormField.columns), classes: { target: styles.columnDialogTarget } })))),
        React.createElement("div", { className: styles.content },
            React.createElement(ResultInfoMessage, { type: ResultInfoMessageType.COPIED_TO_CLIPBOARD, visible: showCopiedToClipboardMessage, onClose: () => setShowCopiedToClipboardMessage(false), className: styles["copied-to-clipboard-message"] }),
            React.createElement(ScrollBar, { onScroll: handleScroll, autoHeight: true, autoHeightMax: "", className: styles.scrollBar, classes: { track: styles["scrollBar-track"], view: styles["scrollBar-view"] } },
                React.createElement(Table, { elementData: elementData, resultSetId: resultSetId, elementId: elementId, columnThreeDotsMenuRefs: columnThreeDotsMenuRefs, viewSelectRefs: viewSelectRefs, setShowCopiedToClipboardMessage: setShowCopiedToClipboardMessage, editMode: editMode, upsert: upsert, remove: remove, move: move }))),
        editMode && (React.createElement("div", { className: styles.footer },
            React.createElement(Button, { style: ButtonStyle.Tertiary, onClick: () => setRowDialogOpen(true), ...getAddRowColumnButtonDefaultProps(TableElementFormField.rows, MAX_ROWS, addRowDisabledMessage) }),
            React.createElement(LearnMoreLink, { className: styles.linkBlock, url: `${HELP_SYSTEM_URL}#cshid=${HelpId.TABULAR}` }),
            rowDialogOpen && (React.createElement(RowDialog, { rows: get(elementData, TableElementFormField.rows), onClose: () => setRowDialogOpen(false), onSubmit: (values) => upsert(values, TableElementFormField.rows), classes: { target: styles.rowDialogTarget } }))))));
};
export default TableElement;
